import { ChangeDetectionStrategy, Component, Input, OnInit, signal, ViewChild, ViewEncapsulation } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { AddressI } from "../../../pojo/AddressI";
import { S25Util } from "../../../util/s25-util";
import { Contact } from "../../../pojo/Contact";
import { S25AddressComponent } from "../../s25-address/s25.address.component";
import { Item } from "../../../pojo/Item";
import { ContactService } from "../../../services/contact.service";
import { Bind } from "../../../decorators/bind.decorator";

@TypeManagerDecorator("s25-ng-object-details-address")
@Component({
    selector: "s25-ng-object-details-address",
    template: `
        @if (canEdit && !editing()) {
            <button class="c-textButton" (click)="editing.set(!editing())">
                <s25-ng-icon [type]="'edit'"></s25-ng-icon>
            </button>
        }
        @if (editing()) {
            <s25-ng-address [(model)]="address"></s25-ng-address>
            <div class="button-group c-margin-top--quarter">
                <s25-ng-button [type]="'primary'" [onClick]="save">Save</s25-ng-button>
                <button class="aw-button aw-button--outline" (click)="editing.set(false)">Cancel</button>
            </div>
        } @else {
            <ng-container *ngTemplateOutlet="contactDetails"></ng-container>
        }
        <s25-ng-fading-success-msg [message]="saveSuccessMsg"></s25-ng-fading-success-msg>

        <!-- Object-specific templates -->
        <ng-template #contactDetails>
            @if (!hasFields) {
                none
            } @else {
                @for (entry of addressArr; track $index) {
                    @let field = entry[0];
                    @let value = entry[1];
                    @if (value) {
                        <div class="field-container">
                            <label [for]="field">{{ field }}</label>
                            <p [id]="field" tabindex="0">{{ value }}</p>
                        </div>
                    }
                }
            }
        </ng-template>
    `,
    styles: `
        @container address (width <= 600px) {
            .editable-address > div,
            .field-container {
                display: flex !important;
                flex-direction: column;
                align-items: flex-start !important;
                gap: 5px;
                margin-bottom: 10px !important;

                p {
                    margin-left: 5px;
                }

                .c-input {
                    width: 100%;
                }
            }

            .button-group {
                margin-top: 1rem !important;

                & > * {
                    flex-basis: 50%;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class S25ObjectDetailsAddressComponent implements OnInit {
    @Input({ required: true }) address: AddressI;
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) itemTypeId: number;
    @Input({ required: true }) canEdit: boolean;

    @ViewChild(S25AddressComponent) addressComp: S25AddressComponent;

    editing = signal(false);
    saveSuccessMsg = signal("");
    hasFields: boolean;
    addressArr: AddressEntry<AddressI>;

    ngOnInit() {
        this.setAddressArr();
    }

    @Bind
    async save() {
        this.addressComp.validateEmail();
        if (this.addressComp.emailErrorMsg()) return;
        if (!this.addressUpdated()) return this.editing.set(false);
        const payload = S25Util.addressObjToNode({ ...this.address }, this.address.addressType);
        payload.status = this.hasFields ? "mod" : "new";
        let error;
        switch (this.itemTypeId) {
            case Item.Ids.Contact:
                const [_, err] = await S25Util.Maybe(
                    ContactService.putContactMinimal(this.itemId, { address: [payload] }),
                );
                if (err) error = err;
        }

        if (error) return S25Util.showError(error);
        this.setAddressArr();
        this.editing.set(false);
        this.saveSuccessMsg.set("Details updated successfully.");
    }

    addressUpdated() {
        return this.addressArr.some(([fieldName, fieldValue]) => {
            const standardizedFieldName = fieldName.toLowerCase().split(" ").at(-1);
            return this.address[standardizedFieldName] !== fieldValue;
        });
    }

    setAddressArr() {
        this.addressArr = Object.entries(this.address ?? {})
            .map(([fieldName, fieldValue]) => {
                if (fieldName === "email" && this.address.addressType) {
                    fieldName = `${this.address.addressType === Contact.AddressType.Work ? "Work" : "Home"} Email`;
                } else {
                    fieldName = fieldName[0].toUpperCase() + fieldName.slice(1);
                }

                if (fieldName !== "AddressType") {
                    this.hasFields ||= !!fieldValue;
                    return [fieldName, fieldValue];
                }
            })
            .filter(Boolean) as AddressEntry<AddressI>;
    }
}

type AddressEntry<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];
